import type {
  ApprovalActor,
  ApprovalTemplate,
} from 'ui-product-approvals-common';
import {
  serializeActor,
  type ActorKey,
} from 'ui-product-approvals-common/utils/actorKey';

export const getLockedTemplateApproverActorKeys = (
  template: ApprovalTemplate | undefined
): Set<ActorKey> =>
  new Set(
    (template?.approvalSteps ?? [])
      .filter((step) => step.locked)
      .map((step) => serializeActor(step.actor))
  );

export const getUnavailableTemplateApproverActorKeys = (
  template: ApprovalTemplate | undefined,
  approvers: ApprovalActor[]
): Set<ActorKey> => {
  if (!template?.approvalSteps.length) {
    return new Set();
  }

  const availableActorKeys = new Set(
    approvers.map((approver) => serializeActor(approver.actor))
  );

  return new Set(
    template.approvalSteps
      .filter((step) => !availableActorKeys.has(serializeActor(step.actor)))
      .map((step) => serializeActor(step.actor))
  );
};

export const hasUnavailableTemplateApprover = (
  template: ApprovalTemplate | undefined,
  approvers: ApprovalActor[]
): boolean =>
  getUnavailableTemplateApproverActorKeys(template, approvers).size > 0;

export const getInitialStepGroups = (
  template: ApprovalTemplate | undefined,
  approvers: ApprovalActor[],
  defaultAgentApprover?: ApprovalActor
): ApprovalActor[][] => {
  const approversByActorKey = new Map(
    approvers.map((approver) => [serializeActor(approver.actor), approver])
  );
  const unorderedApprovers: ApprovalActor[] = [];
  const approversByOrder = new Map<number, ApprovalActor[]>();

  template?.approvalSteps.forEach((templateStep) => {
    const approver = approversByActorKey.get(
      serializeActor(templateStep.actor)
    );
    if (!approver) {
      return;
    }
    if (templateStep.order === undefined) {
      unorderedApprovers.push(approver);
      return;
    }
    const stepApprovers = approversByOrder.get(templateStep.order) ?? [];
    stepApprovers.push(approver);
    approversByOrder.set(templateStep.order, stepApprovers);
  });

  const orderedGroups = [...approversByOrder.entries()]
    .sort(([orderA], [orderB]) => orderA - orderB)
    .map(([, stepApprovers]) => stepApprovers);
  const stepGroups =
    unorderedApprovers.length > 0
      ? [unorderedApprovers, ...orderedGroups]
      : orderedGroups;

  if (!defaultAgentApprover) {
    return stepGroups;
  }

  if (stepGroups.length === 0) {
    return [[defaultAgentApprover]];
  }

  const [firstStep, ...remainingSteps] = stepGroups;
  return [
    firstStep.some(
      (approver) =>
        serializeActor(approver.actor) ===
        serializeActor(defaultAgentApprover.actor)
    )
      ? firstStep
      : [...firstStep, defaultAgentApprover],
    ...remainingSteps,
  ];
};

export const getLockedTemplateStageIndexes = (
  template: ApprovalTemplate | undefined,
  approvers: ApprovalActor[],
  renderedStepGroups?: ApprovalActor[][]
): Set<number> => {
  // An approval may no longer contain every template stage. In that case,
  // map a stage lock to its current group by the locked template actor rather
  // than the template's original ordinal.
  if (renderedStepGroups) {
    const lockedTemplateActorKeys = new Set(
      (template?.approvalSteps ?? [])
        .filter((step) => step.stageLocked)
        .map((step) => serializeActor(step.actor))
    );

    return new Set(
      renderedStepGroups.flatMap((stepGroup, index) =>
        stepGroup.some((approver) =>
          lockedTemplateActorKeys.has(serializeActor(approver.actor))
        )
          ? [index]
          : []
      )
    );
  }

  const availableActorKeys = new Set(
    approvers.map((approver) => serializeActor(approver.actor))
  );
  const templateSteps = (template?.approvalSteps ?? []).filter((step) =>
    availableActorKeys.has(serializeActor(step.actor))
  );
  const unorderedSteps = templateSteps.filter(
    (step) => step.order === undefined
  );
  const orderedStages = new Map<number, ApprovalTemplate['approvalSteps']>();

  templateSteps.forEach((step) => {
    if (step.order === undefined) {
      return;
    }
    const stage = orderedStages.get(step.order) ?? [];
    stage.push(step);
    orderedStages.set(step.order, stage);
  });

  return new Set([
    ...(unorderedSteps.some((step) => step.stageLocked) ? [0] : []),
    ...[...orderedStages.entries()]
      .sort(([firstOrder], [secondOrder]) => firstOrder - secondOrder)
      .flatMap(([, steps], index) =>
        steps.some((step) => step.stageLocked)
          ? [index + (unorderedSteps.length > 0 ? 1 : 0)]
          : []
      ),
  ]);
};
